/** Entry point: locale, UI wiring, and the voice conversation lifecycle. */
import { Conversation } from "@elevenlabs/client";
import { setupFeaturesDrawer } from "./drawer";
import { setupOnboarding } from "./onboarding";
import { createOrb, type OrbMode } from "./orb";
import { createClientTools } from "./tools";
import { getLocale, strings, type StringKey } from "./ui.strings";
import { setupVoicePicker } from "./voice-picker";

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/* ── Locale (URL slug: /fr or /es) ───────────────────── */
const locale = getLocale();
document.documentElement.lang = locale;
document.title = strings.page_title;
for (const element of document.querySelectorAll<HTMLElement>("[data-i18n]")) {
  const key = element.dataset.i18n as StringKey;
  if (strings[key]) element.textContent = strings[key];
}
document
  .querySelector(`.lang-switch a[data-lang="${locale}"]`)
  ?.setAttribute("data-active", "true");

/* ── UI modules ──────────────────────────────────────── */
const onboarding = setupOnboarding({ locale });
const voicePicker = setupVoicePicker();
setupFeaturesDrawer({ locale });

const toggleButton = byId<HTMLButtonElement>("toggle");
const toggleLabel = byId<HTMLSpanElement>("toggle-label");
const agentStatus = byId<HTMLParagraphElement>("agent-status");
const messagesEl = byId<HTMLElement>("messages");
const hint = byId<HTMLParagraphElement>("hint");
const transcriptPanel = byId<HTMLElement>("transcript-panel");
const captionsEl = byId<HTMLDivElement>("captions");

const orb = createOrb({ canvas: byId<HTMLCanvasElement>("orb") });

let conversation: Conversation | null = null;
let currentMode: OrbMode = "idle";
let lastUserSpeechEnd = 0;

/* ── Real audio volume → orb deformation ─────────────── */
const pollVolume = () => {
  if (conversation) {
    const level =
      currentMode === "speaking"
        ? conversation.getOutputVolume()
        : conversation.getInputVolume();
    orb.setLevel({ level });
    requestAnimationFrame(pollVolume);
  } else {
    orb.setLevel({ level: 0 });
  }
};

/* ── UI helpers ──────────────────────────────────────── */
const setStatus = ({ key, live }: { key: StringKey; live?: boolean }) => {
  agentStatus.textContent = strings[key];
  agentStatus.classList.toggle("live", Boolean(live));
};

const setButton = ({ state, labelKey }: { state: string; labelKey: StringKey }) => {
  toggleButton.dataset.state = state;
  toggleLabel.textContent = strings[labelKey];
  hint.textContent = state === "live" ? strings.hint_live : strings.hint_idle;
};

const setMode = ({ mode }: { mode: OrbMode }) => {
  currentMode = mode;
  orb.setMode({ mode });
};

const addBubble = ({ source, text }: { source: "user" | "ai"; text: string }) => {
  const row = document.createElement("div");
  row.className = source === "user" ? "message message-user" : "message message-agent";
  const bubble = document.createElement("div");
  bubble.className = source === "user" ? "bubble bubble-user" : "bubble bubble-agent";
  bubble.textContent = text;
  row.append(bubble);
  messagesEl.append(row);
  messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "smooth" });
};

/** Captions under the orb: last 3 exchanges, newest in front. */
const MAX_CAPTIONS = 3;

const addCaption = ({ source, text }: { source: "user" | "ai"; text: string }) => {
  for (const previous of captionsEl.children) previous.classList.add("past");

  const caption = document.createElement("p");
  caption.className = source === "user" ? "caption caption-user" : "caption caption-agent";
  caption.textContent = source === "user" ? `${strings.speaker_user} — ${text}` : text;
  captionsEl.append(caption);

  const active = [...captionsEl.children].filter((el) => !el.classList.contains("leaving"));
  for (const oldest of active.slice(0, Math.max(0, active.length - MAX_CAPTIONS))) {
    oldest.classList.add("leaving");
    setTimeout(() => oldest.remove(), 450);
  }
};

const addLatencyChip = ({ milliseconds }: { milliseconds: number }) => {
  const chip = document.createElement("span");
  chip.className = "latency-chip";
  chip.textContent = `${milliseconds} ms`;
  messagesEl.append(chip);
};

/** Chip shown when the agent uses a tool (transcript + captions). */
const notifyToolCall = ({ label }: { label: string }) => {
  const chip = document.createElement("span");
  chip.className = "latency-chip";
  chip.textContent = label;
  messagesEl.append(chip);
  messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "smooth" });

  const caption = document.createElement("p");
  caption.className = "caption caption-tool";
  caption.textContent = label;
  captionsEl.append(caption);
  setTimeout(() => {
    caption.classList.add("leaving");
    setTimeout(() => caption.remove(), 450);
  }, 2600);
};

const clientTools = createClientTools({ notify: notifyToolCall });

/* ── Conversation lifecycle ──────────────────────────── */
const startConversation = async () => {
  const profile = onboarding.current();
  if (!profile) {
    onboarding.open();
    return;
  }
  setStatus({ key: "status_connecting" });
  setButton({ state: "connecting", labelKey: "stop" });

  await navigator.mediaDevices.getUserMedia({ audio: true });
  const response = await fetch(`/api/signed-url?lang=${locale}`, {
    headers: profile.accessCode ? { "x-access-code": profile.accessCode } : {},
  });
  if (!response.ok) throw new Error(await response.text());
  const { signedUrl } = (await response.json()) as { signedUrl: string };

  voicePicker.setLocked({ locked: true });
  conversation = await Conversation.startSession({
    signedUrl,
    clientTools,
    overrides: { tts: { voiceId: voicePicker.voiceId() } },
    dynamicVariables: { user_name: profile.firstName },
    onConnect: () => {
      setStatus({ key: "status_listening", live: true });
      setButton({ state: "live", labelKey: "stop" });
      setMode({ mode: "listening" });
      requestAnimationFrame(pollVolume);
    },
    onDisconnect: () => {
      setStatus({ key: "status_ended" });
      setButton({ state: "idle", labelKey: "start" });
      setMode({ mode: "idle" });
      voicePicker.setLocked({ locked: false });
      conversation = null;
    },
    onModeChange: ({ mode }) => {
      const speaking = mode === "speaking";
      if (speaking && lastUserSpeechEnd > 0) {
        addLatencyChip({ milliseconds: Math.round(performance.now() - lastUserSpeechEnd) });
        lastUserSpeechEnd = 0;
      }
      if (!speaking) lastUserSpeechEnd = performance.now();
      setMode({ mode: speaking ? "speaking" : "listening" });
      setStatus({ key: speaking ? "status_speaking" : "status_listening", live: true });
    },
    onMessage: ({ source, message }) => {
      addBubble({ source, text: message });
      addCaption({ source, text: message });
    },
    onError: (message) => {
      console.error("Conversation error:", message);
      setStatus({ key: "status_error" });
    },
  });
};

const stopConversation = async () => {
  await conversation?.endSession();
  conversation = null;
  setStatus({ key: "status_ended" });
  setButton({ state: "idle", labelKey: "start" });
  setMode({ mode: "idle" });
};

toggleButton.addEventListener("click", () => {
  const action = conversation ? stopConversation : startConversation;
  action().catch((error) => {
    console.error(error);
    setStatus({ key: "status_error" });
    setButton({ state: "idle", labelKey: "start" });
    setMode({ mode: "idle" });
    conversation = null;
  });
});

/* ── Transcript panel ────────────────────────────────── */
const setTranscriptOpen = ({ open }: { open: boolean }) => {
  transcriptPanel.dataset.open = String(open);
};
byId<HTMLButtonElement>("transcript-toggle").addEventListener("click", () =>
  setTranscriptOpen({ open: true }),
);
byId<HTMLButtonElement>("transcript-close").addEventListener("click", () =>
  setTranscriptOpen({ open: false }),
);
